/**
 * TaskStageUpdateActionCard — a proposed stage change on an EXISTING
 * `ahoy_task`, rendered as the canonical TaskCard (the same card the create
 * action and the record slideout's Tasks section use), with the Status row
 * showing the `current → proposed` diff in place — the same diff language the
 * record slideout uses for projected changes.
 *
 * The two sides of that diff come from different reads, and the distinction
 * is the whole point (docs/claude/FRONTEND_PATTERNS.md § Live vs. projected):
 *
 *   - current  → `useCrmObjectDiff().current`, the live record with NO
 *     proposal projected onto it.
 *   - proposed → this action's own `target_entity_id`; the action IS the
 *     proposal.
 *
 * Editing follows the create card's in-place model: clicking the Status row
 * opens the stage editor on the PROPOSED side and commits by rewriting THIS
 * action's payload — no accordion edit session, so the shell edit pencil is
 * suppressed for this subcase in `isEditableAction`. Every other value is
 * deliberately read-only: this card is about the proposal, and an inline
 * title/description editor here would silently write to the live CRM record.
 *
 * The task exists, so its title is a click-to-open affordance for the task
 * slideout, and the card hydrates title / description / due date / assignee
 * from the CRM store (fetched with associations — the assignee and stage
 * ride the association list).
 */

import { useCallback, useMemo } from 'react';

import { useCrmObjectDiff } from '@/lib/crm/hooks';
import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';
import { useCrmSlideout } from '@/lib/crm/object-cards/useCrmSlideout';
import {
  extractMemoData,
  getMemoAssociationNames,
} from '@/lib/crm/record/memo/notes.utils';
import { TASK_ENTITY_TYPE } from '@/lib/crm/record/task/task.constants';
import { TaskCard } from '@/lib/crm/record/task/TaskCard';
import { useCrmObject } from '@/lib/crm/store';

import type {
  AssociationAddedEntityPayload,
  ProposedAction,
} from '../../lib/proposals/core/types';
import type { ActionRendererProps } from '../../lib/proposals/registry/types';
// The local `function findAssociationTarget` duplicate was removed from this
// file; the implementation now lives in `./shared/utils.ts` and is imported
// below.
import {
  ActionCardContent,
  findAssociationTarget,
  useActionPayloadSave,
} from './shared';

interface TaskStageUpdateActionCardProps {
  action: ProposedAction<AssociationAddedEntityPayload>;
  mode: ActionRendererProps['mode'];
  enrichedData: ActionRendererProps['enrichedData'];
  proposalId?: number;
  orgId?: number;
  onUpdate?: ActionRendererProps['onUpdate'];
}

export function TaskStageUpdateActionCard({
  action,
  enrichedData,
  proposalId,
  orgId,
  onUpdate,
}: TaskStageUpdateActionCardProps) {
  const { payload } = action;
  const taskEntityId = payload.entity_id;
  const stageAssociationTypeName = payload.payload.association_type_name;
  const proposedStageEntityId = payload.payload.target_entity_id || null;

  const { owner: ownerAssociationName } =
    getMemoAssociationNames(TASK_ENTITY_TYPE);

  // `orgId` is optional on this card; the hooks are bound to 0 in that case
  // and the card returns null below before anything renders.
  const { schema } = usePropertySchema(orgId ?? 0, TASK_ENTITY_TYPE);
  const { associationTypes } = useAssociationTypes(orgId ?? 0);
  const assigneeAssociationType = useMemo(
    () =>
      associationTypes.find(
        at => at.association_type_name === ownerAssociationName
      ),
    [associationTypes, ownerAssociationName]
  );

  // Hydrate the task with its association list — the assignee and stage ride
  // it, and the association batcher deliberately excludes associations.
  // `enabled` gates the fetch on a real orgId (the ?? 0 binding must never
  // reach the network).
  const { object: taskObject } = useCrmObject(
    orgId ?? 0,
    TASK_ENTITY_TYPE,
    taskEntityId || undefined,
    { includeAssociations: true, enabled: Boolean(orgId) }
  );

  // The CURRENT stage must come from the live read: `taskObject` above is the
  // store's projected view (an active proposal overlay is merged into it), so
  // reading the stage off it would eventually show the proposed value on both
  // sides of the diff.
  const { current: liveObject } = useCrmObjectDiff(
    taskEntityId || undefined,
    orgId
  );
  const currentStageEntityId =
    findAssociationTarget(liveObject, stageAssociationTypeName) ?? null;

  const task = useMemo(() => {
    if (taskObject) {
      return extractMemoData(
        taskObject,
        assigneeAssociationType?.association_type_name ?? null,
        stageAssociationTypeName
      );
    }
    // Store still hydrating — render what the action knows so the card
    // paints immediately; the store subscription fills the rest in.
    return {
      entity_id: taskEntityId,
      title: enrichedData?.object_label ?? '',
      content: '',
      created_at: '',
      updated_at: '',
      owner_entity_id: null,
      last_event_seq: 0,
      date: undefined,
      datetime: undefined,
      stage_entity_id: null,
      creator_entity_id: null,
      is_private: false,
    };
  }, [
    taskObject,
    assigneeAssociationType?.association_type_name,
    stageAssociationTypeName,
    taskEntityId,
    enrichedData?.object_label,
  ]);

  const openTaskSlideout = useCrmSlideout(taskEntityId || undefined);

  const { savePayload } = useActionPayloadSave({
    orgId,
    proposalId,
    actionId: action.id,
    onUpdate,
  });

  const handleStageChange = useCallback(
    async (_taskEntityId: string, newStageEntityId: string | null) => {
      // The proposal is an `ahoy_association_added` action, so an empty
      // `target_entity_id` is an invalid add — the same reason a proposed
      // task's assignee row can't be cleared (see
      // TaskFieldRows.allowAssigneeClear). Persisting `''` would also flip the
      // card to render "(removed)" via `proposedStageEntityId`. Clearing the
      // proposed stage has no honest representation here, so refuse the write
      // and leave the existing proposed target in place.
      if (!newStageEntityId) {
        return;
      }
      await savePayload({
        ...payload,
        payload: {
          ...payload.payload,
          target_entity_id: newStageEntityId,
        },
      });
    },
    [payload, savePayload]
  );

  // ActionRenderer only passes `onUpdate` when the action supports editing
  // AND mode === 'full', so its presence is the edit gate (same contract as
  // TaskCreateActionCard).
  const canEdit = Boolean(onUpdate && proposalId && orgId);

  if (!orgId) {
    return null;
  }

  return (
    <ActionCardContent
      contextMessage={payload.context_message}
      orgId={orgId}
      resolvedSlots={action.enriched_data?.resolved_slots}
    >
      <TaskCard
        task={task}
        orgId={orgId}
        stageEntityId={currentStageEntityId}
        proposedStageEntityId={proposedStageEntityId}
        ownerEntityId={task.owner_entity_id}
        dueDateSchema={schema['ahoy_date']}
        assigneeAssociationType={assigneeAssociationType}
        onStageChange={canEdit ? handleStageChange : undefined}
        onTitleClick={openTaskSlideout}
      />
    </ActionCardContent>
  );
}
